import React, { useState } from 'react'
import styled from 'styled-components'
import { useToast } from '@sketch/toasts'
import {
  ConfirmationDialog,
  ModalInjectedProps,
  RadioButton,
} from '@sketch/components'
import { DeletePreviousShareVersionsMutationVariables } from '@sketch/gql-types'
import { pluralize } from '@sketch/utils'
import { usePreviousVersionsDelete } from '../../operations'

type PreserveVersionsOption =
  DeletePreviousShareVersionsMutationVariables['preserveVersions']

export interface DeletePreviousVersionsModalProps extends ModalInjectedProps {
  shareName: string
  shareIdentifier: string
  versionIdentifier: string
}

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`

export const DeletePreviousVersionsModal: React.FC<
  DeletePreviousVersionsModalProps
> = ({
  hideModal,
  isModalOpen,
  shareName,
  shareIdentifier,
  versionIdentifier,
}) => {
  const { showToast } = useToast()
  const [preserveVersions, setPreserveVersions] =
    useState<PreserveVersionsOption>('STARRED_ONLY')

  const [deletePreviousVersions, { loading }] = usePreviousVersionsDelete({
    shareIdentifier,
    versionIdentifier,
    preserveVersions,
    onError: message => {
      showToast(message, 'negative')
    },
    onCompleted: deletedCount => {
      const versionText = pluralize('version', 'versions', deletedCount)
      showToast(`${deletedCount} ${versionText} of "${shareName}" deleted`)
      hideModal()
    },
  })

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPreserveVersions(event.target.value as PreserveVersionsOption)
  }

  return (
    <ConfirmationDialog
      title="Delete Previous Versions?"
      hideModal={hideModal}
      isModalOpen={isModalOpen}
      cancelButton={{
        disabled: loading,
      }}
      confirmButton={{
        text: 'Delete Versions',
        variant: 'negative',
        loading,
        disabled: loading,
      }}
      onConfirm={() => deletePreviousVersions()}
    >
      <p>
        This will delete all versions of &ldquo;<strong>{shareName}</strong>
        &rdquo; before this version. You can&apos;t undo this action.
      </p>
      <RadioGroup>
        <RadioButton
          name="preservation-option"
          value="STARRED_ONLY"
          checked={preserveVersions === 'STARRED_ONLY'}
          onChange={handleOptionChange}
          label="Preserve starred versions"
          help="Delete older versions but keep starred versions"
        />
        <RadioButton
          name="preservation-option"
          value="STARRED_OR_WITH_DESCRIPTION"
          checked={preserveVersions === 'STARRED_OR_WITH_DESCRIPTION'}
          onChange={handleOptionChange}
          label="Preserve starred versions and versions with descriptions"
          help="Delete older versions but keep starred versions and versions with descriptions"
        />
        <RadioButton
          name="preservation-option"
          value="NONE"
          checked={preserveVersions === 'NONE'}
          onChange={handleOptionChange}
          label="Delete all previous versions"
          help="Only keep this version and newer versions"
        />
      </RadioGroup>
    </ConfirmationDialog>
  )
}
